/**
 * Permissions Drawer
 *
 * Displays role descriptions and their permissions.
 * Static content, no API calls.
 */

import { Crown, Shield, UserCog, Wrench, Eye } from 'lucide-react';
import { Drawer } from '@/components/ui';

interface PermissionsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const roles = [
  {
    name: 'OWNER',
    label: 'Proprietaire',
    icon: Crown,
    color: 'text-amber-600 bg-amber-50',
    description: 'Controle total sur l\'etablissement',
    permissions: [
      'Toutes les permissions ADMIN',
      'Transfert de propriete de l\'etablissement',
    ],
  },
  {
    name: 'ADMIN',
    label: 'Administrateur',
    icon: Shield,
    color: 'text-blue-600 bg-blue-50',
    description: 'Gestion complete de l\'etablissement',
    permissions: [
      'Gestion des utilisateurs (creation, modification, suppression)',
      'Configuration des parametres',
      'Gestion des fournisseurs',
      'Synchronisation caisse (POS)',
      'Suppression des produits et recettes',
      'Toutes les permissions MANAGER',
    ],
  },
  {
    name: 'MANAGER',
    label: 'Responsable',
    icon: UserCog,
    color: 'text-purple-600 bg-purple-50',
    description: 'Validation et supervision des operations',
    permissions: [
      'Validation des inventaires et bons de livraison',
      'Envoi des commandes aux fournisseurs',
      'Expedition et reception des transferts',
      'Suppression des documents (inventaires, BL, commandes, pertes)',
      'Acces aux donnees financieres (PMP, couts, valeurs)',
      'Acces aux rapports complets',
      'Creation de produits et recettes',
      'Consultation des logs d\'audit',
      'Toutes les permissions OPERATOR',
    ],
  },
  {
    name: 'OPERATOR',
    label: 'Operateur',
    icon: Wrench,
    color: 'text-green-600 bg-green-50',
    description: 'Saisie quotidienne des operations',
    permissions: [
      'Consultation du stock (sans donnees financieres)',
      'Creation d\'inventaires et modification (brouillons)',
      'Creation de bons de livraison',
      'Creation de commandes fournisseur',
      'Declaration de pertes',
      'Demande de transferts inter-sites',
      'Consultation des ventes POS',
    ],
  },
  {
    name: 'VIEWER',
    label: 'Consultation',
    icon: Eye,
    color: 'text-neutral-600 bg-neutral-100',
    description: 'Lecture seule',
    permissions: [
      'Consultation du stock (sans donnees financieres)',
      'Consultation des inventaires, BL, commandes, pertes, transferts',
      'Consultation des produits et recettes',
      'Consultation des rapports (sans donnees financieres)',
      'Aucune modification possible',
    ],
  },
];

export function PermissionsDrawer({ isOpen, onClose }: PermissionsDrawerProps) {
  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title="Roles et Permissions"
      description="Description des differents niveaux d'acces"
      size="default"
    >
      <div className="space-y-6">
        {roles.map((role) => {
          const Icon = role.icon;
          return (
            <div key={role.name} className="rounded-lg border border-neutral-200 p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${role.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-dark-500">{role.label}</h3>
                  <span className="text-xs text-neutral-400 font-mono">{role.name}</span>
                </div>
              </div>
              <p className="text-sm text-neutral-500 mb-3">{role.description}</p>
              <ul className="space-y-1.5 text-sm text-neutral-600">
                {role.permissions.map((permission, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-neutral-400 mt-1">-</span>
                    <span>{permission}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </Drawer>
  );
}
